import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams, Link, Navigate } from "react-router-dom";
import { Upload, X, ArrowLeft, Lock, Sparkles, ArrowRight, MapPin, Loader2 } from "lucide-react";
import { api, fileUrl, formatApiError } from "../lib/api";
import { toast } from "sonner";
import LocationMapPicker from "../components/LocationMapPicker";
import { useAuth } from "../context/AuthContext";
import { isServiceRole } from "../lib/roles";
import { startListingCreditCheckout } from "../lib/razorpay";

const AMENITY_OPTS = ["Swimming Pool", "Gym", "24/7 Security", "Power Backup", "Clubhouse", "Garden", "Parking", "Lift", "Furnished", "Pet Friendly", "Solar Power", "Smart Home"];

const EMPTY_FORM = {
    title: "",
    description: "",
    listing_type: "sale",
    property_type: "residential",
    subtype: "",
    price: "",
    bhk: "",
    bathrooms: "",
    area_sqft: "",
    city: "",
    state: "Maharashtra",
    locality: "",
    address: "",
    pincode: "",
    contact_phone: "",
    amenities: [],
    images: [],
    latitude: null,
    longitude: null,
};

export default function AddProperty() {
    const nav = useNavigate();
    const { id } = useParams();
    const { user } = useAuth();
    const isEdit = Boolean(id);
    const [submitting, setSubmitting] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, percent: 0, name: "" });
    const [pendingPreviews, setPendingPreviews] = useState([]); // [{name, url}] local blob URLs while server upload runs
    const [loading, setLoading] = useState(isEdit);
    const [form, setForm] = useState(EMPTY_FORM);
    const [quota, setQuota] = useState(null); // { used, limit, needs_upgrade, has_paid_plan }
    const [upgradeOpen, setUpgradeOpen] = useState(false);

    // Strict role guard — service professionals never list properties. Marked here so the
    // rest of the hooks below stay declared unconditionally (React rules-of-hooks).
    const blockedForServiceRole = Boolean(user && isServiceRole(user.role));

    // Fetch property quota for new listings so we can block/nudge upfront.
    useEffect(() => {
        if (isEdit || blockedForServiceRole) return;
        api.get("/me/property-quota")
            .then((r) => {
                setQuota(r.data);
                if (r.data?.needs_upgrade) setUpgradeOpen(true);
            })
            .catch(() => { /* soft-fail; server will still enforce */ });
    }, [isEdit, blockedForServiceRole]);

    useEffect(() => {
        if (!isEdit) return;
        api.get(`/properties/${id}`)
            .then((r) => {
                const p = r.data || {};
                setForm({
                    ...EMPTY_FORM,
                    ...p,
                    subtype: p.subtype || "",
                    locality: p.locality || "",
                    address: p.address || "",
                    pincode: p.pincode || "",
                    contact_phone: p.contact_phone || "",
                    price: String(p.price ?? ""),
                    bhk: p.bhk != null ? String(p.bhk) : "",
                    bathrooms: p.bathrooms != null ? String(p.bathrooms) : "",
                    area_sqft: p.area_sqft != null ? String(p.area_sqft) : "",
                    amenities: p.amenities || [],
                    images: p.images || [],
                });
            })
            .catch((err) => {
                toast.error(formatApiError(err.response?.data?.detail) || "Could not load property");
                nav("/dashboard");
            })
            .finally(() => setLoading(false));
    }, [id, isEdit, nav]);

    const onUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        // Show local previews immediately so the user knows something is happening.
        const previews = files.map((f) => ({ name: f.name, url: URL.createObjectURL(f) }));
        setPendingPreviews(previews);
        setUploading(true);
        setUploadProgress({ current: 0, total: files.length, percent: 0, name: files[0].name });
        try {
            const urls = [];
            for (let i = 0; i < files.length; i++) {
                const f = files[i];
                setUploadProgress({ current: i + 1, total: files.length, percent: 0, name: f.name });
                const fd = new FormData();
                fd.append("file", f);
                const { data } = await api.post("/properties/upload-image", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                    onUploadProgress: (evt) => {
                        if (!evt.total) return;
                        const pct = Math.round((evt.loaded * 100) / evt.total);
                        setUploadProgress((p) => ({ ...p, percent: pct }));
                    },
                });
                urls.push(data.url);
                // Reveal each server image as soon as it lands, and drop its local preview
                setForm((f2) => ({ ...f2, images: [...f2.images, data.url] }));
                setPendingPreviews((prev) => {
                    // Revoke the blob URL we just replaced so memory is freed
                    try { URL.revokeObjectURL(prev[0]?.url); } catch { /* ignore */ }
                    return prev.slice(1);
                });
            }
            toast.success(`${files.length} photo${files.length > 1 ? "s" : ""} uploaded successfully — you can publish now`);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Upload failed — please retry");
        } finally {
            // Revoke blob URLs to free memory
            previews.forEach((p) => { try { URL.revokeObjectURL(p.url); } catch { /* ignore */ } });
            setPendingPreviews([]);
            setUploading(false);
            setUploadProgress({ current: 0, total: 0, percent: 0, name: "" });
            // Reset the input so choosing the same file again re-triggers change
            try { e.target.value = ""; } catch { /* ignore */ }
        }
    };

    const removeImg = (i) => setForm((f) => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }));

    const toggleAmenity = (a) => setForm((f) => ({
        ...f,
        amenities: f.amenities.includes(a) ? f.amenities.filter((x) => x !== a) : [...f.amenities, a],
    }));

    const onSubmit = async (e) => {
        e.preventDefault();
        // Guard: don't allow publishing while a photo upload is still in progress
        if (uploading) {
            toast.error("Please wait — photos are still uploading");
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                title: form.title,
                description: form.description,
                listing_type: form.listing_type,
                property_type: form.property_type,
                subtype: form.subtype || null,
                price: Number(form.price),
                bhk: form.bhk ? Number(form.bhk) : null,
                bathrooms: form.bathrooms ? Number(form.bathrooms) : null,
                area_sqft: form.area_sqft ? Number(form.area_sqft) : null,
                city: form.city,
                state: form.state,
                locality: form.locality || null,
                address: form.address || null,
                pincode: form.pincode || null,
                contact_phone: form.contact_phone || null,
                amenities: form.amenities,
                images: form.images,
                latitude: form.latitude ?? null,
                longitude: form.longitude ?? null,
            };
            if (isEdit) {
                const { data } = await api.put(`/properties/${id}`, payload);
                toast.success("Property updated!");
                nav(`/properties/${data.id || id}`);
            } else {
                const { data } = await api.post("/properties", payload);
                toast.success("Property listed!");
                nav(`/properties/${data.id}`);
            }
        } catch (err) {
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail;
            if (!isEdit && status === 402) {
                setUpgradeOpen(true);
                toast.error(detail || "Upgrade required to add more properties");
            } else {
                toast.error(formatApiError(detail));
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center text-navy-500/70" data-testid="ap-loading">
                Loading property…
            </div>
        );
    }

    // Service pros redirect happens after all hooks so React rules-of-hooks stay clean.
    if (blockedForServiceRole) {
        return <Navigate to="/my-services" replace />;
    }

    return (
        <div className="min-h-screen bg-[#FAFAF7]">
            <UpgradeModal
                open={upgradeOpen}
                onClose={() => setUpgradeOpen(false)}
                usedCount={quota?.used}
                availableCredits={quota?.available_credits || 0}
                creditPriceInr={quota?.credit_price_inr || 99}
                onCreditPurchased={() => {
                    // Refresh quota so the banner + submit-button state update immediately.
                    api.get("/me/property-quota").then((r) => setQuota(r.data)).catch(() => {});
                }}
            />
            {quota?.needs_upgrade && !isEdit && (
                <div className="bg-gold-500/10 border-b border-gold-500/30" data-testid="ap-quota-banner">
                    <div className="max-w-4xl mx-auto px-6 lg:px-8 py-3 flex items-center gap-3 text-sm text-navy-500">
                        <Lock className="w-4 h-4 text-gold-700 shrink-0" />
                        <span className="flex-1">
                            <strong>Your free listing is used.</strong>{" "}
                            List your next property for just ₹{quota?.credit_price_inr || 99} or subscribe for unlimited.
                        </span>
                        <button
                            type="button"
                            onClick={() => setUpgradeOpen(true)}
                            data-testid="ap-quota-upgrade-btn"
                            className="px-3 py-1.5 rounded-md bg-gold-500 text-navy-900 text-xs font-extrabold uppercase tracking-wider hover:bg-gold-400"
                        >
                            Upgrade
                        </button>
                    </div>
                </div>
            )}
            <div className="bg-white border-b border-navy-500/10">
                <div className="max-w-4xl mx-auto px-6 lg:px-8 py-10">
                    <button onClick={() => nav(-1)} className="text-sm text-navy-500/60 hover:text-gold-600 inline-flex items-center gap-2 mb-4" data-testid="ap-back">
                        <ArrowLeft className="w-4 h-4" /> Back
                    </button>
                    <div className="label-overline mb-2">{isEdit ? "Edit Listing" : "New Listing"}</div>
                    <h1 className="font-heading text-3xl lg:text-4xl font-bold text-navy-500">{isEdit ? "Edit property details" : "Add a property"}</h1>
                </div>
            </div>
            <form onSubmit={onSubmit} className="max-w-4xl mx-auto px-6 lg:px-8 py-10 space-y-8" data-testid={isEdit ? "edit-property-form" : "add-property-form"}>
                <Card title="Basics">
                    <Field label="Title" required value={form.title} onChange={(v) => setForm({...form, title:v})} testid="ap-title" placeholder="e.g. Luxury 3 BHK in Civil Lines" />
                    <Field label="Description" required textarea value={form.description} onChange={(v) => setForm({...form, description:v})} testid="ap-desc" placeholder="Highlight the key features…" />
                    <div className="grid grid-cols-2 gap-3">
                        <Select label="For" value={form.listing_type} onChange={(v) => setForm({...form, listing_type:v})} testid="ap-listing"
                            options={[{v:"sale",l:"Sale"},{v:"rent",l:"Rent"},{v:"pg",l:"PG / Hostel"}]} />
                        <Select label="Property type" value={form.property_type} onChange={(v) => setForm({...form, property_type:v})} testid="ap-type"
                            options={[{v:"residential",l:"Residential"},{v:"commercial",l:"Commercial"},{v:"plot",l:"Plot"},{v:"farmland",l:"Farmland"}]} />
                    </div>
                </Card>

                <Card title="Pricing & Specs">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <Field label="Price (₹)" required type="number" value={form.price} onChange={(v) => setForm({...form, price:v})} testid="ap-price" />
                        <Field label="BHK" type="number" value={form.bhk} onChange={(v) => setForm({...form, bhk:v})} testid="ap-bhk" />
                        <Field label="Bathrooms" type="number" value={form.bathrooms} onChange={(v) => setForm({...form, bathrooms:v})} testid="ap-bath" />
                        <Field label="Area (sqft)" type="number" value={form.area_sqft} onChange={(v) => setForm({...form, area_sqft:v})} testid="ap-area" />
                    </div>
                </Card>

                <Card title="Location">
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="City" required value={form.city} onChange={(v) => setForm({...form, city:v})} testid="ap-city" />
                        <Field label="State" required value={form.state} onChange={(v) => setForm({...form, state:v})} testid="ap-state" />
                        <Field label="Locality" value={form.locality} onChange={(v) => setForm({...form, locality:v})} testid="ap-locality" />
                        <Field label="Pincode" value={form.pincode} onChange={(v) => setForm({...form, pincode:v})} testid="ap-pincode" />
                    </div>
                    <Field label="Address" value={form.address} onChange={(v) => setForm({...form, address:v})} testid="ap-address" placeholder="Street, landmark, building name" />
                    <LocationPreview form={form} onSet={(lat, lng) => setForm((f) => ({...f, latitude: lat, longitude: lng}))} />
                    <Field label="Contact phone" value={form.contact_phone} onChange={(v) => setForm({...form, contact_phone:v})} testid="ap-phone" />
                </Card>

                <Card title="Amenities">
                    <div className="flex flex-wrap gap-2">
                        {AMENITY_OPTS.map((a) => (
                            <button
                                key={a} type="button"
                                onClick={() => toggleAmenity(a)}
                                data-testid={`ap-amenity-${a.replace(/\s+/g,'-').toLowerCase()}`}
                                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                                    form.amenities.includes(a)
                                        ? "bg-gold-500 text-white border-gold-500"
                                        : "border-navy-500/15 text-navy-500/70 hover:border-gold-500/50"
                                }`}
                            >{a}</button>
                        ))}
                    </div>
                </Card>

                <Card title="Photos">
                    {uploading && (
                        <div className="mb-3 rounded-xl border border-gold-500/40 bg-gold-500/5 p-3" data-testid="ap-upload-progress" aria-live="polite">
                            <div className="flex items-center gap-2">
                                <Loader2 className="w-4 h-4 text-gold-700 animate-spin shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-xs font-extrabold text-navy-500">
                                        Uploading photo {uploadProgress.current} of {uploadProgress.total}…
                                    </div>
                                    <div className="text-[11px] text-navy-500/60 truncate mt-0.5">
                                        {uploadProgress.name} · <strong className="text-gold-700">Please wait — do NOT tap Publish yet.</strong>
                                    </div>
                                </div>
                                <span className="text-[11px] font-extrabold text-gold-700 tabular-nums">{uploadProgress.percent}%</span>
                            </div>
                            <div className="mt-2 h-1.5 rounded-full bg-navy-500/10 overflow-hidden">
                                <div
                                    className="h-full bg-gradient-to-r from-gold-500 to-amber-500 transition-all duration-200"
                                    style={{ width: `${uploadProgress.percent}%` }}
                                />
                            </div>
                        </div>
                    )}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {form.images.map((img, i) => (
                            <div key={i} className="relative rounded-md overflow-hidden aspect-square bg-[#FAFAF7] border border-navy-500/10">
                                <img src={fileUrl(img)} alt="" className="w-full h-full object-cover" />
                                <button type="button" onClick={() => removeImg(i)} data-testid={`ap-remove-img-${i}`} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-[#002657]/85 text-white hover:bg-red-500 flex items-center justify-center">
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        ))}
                        {/* Pending previews from local blob URLs — shown with a spinner overlay so the user knows work is in progress */}
                        {pendingPreviews.map((p, i) => (
                            <div key={`pending-${i}`} className="relative rounded-md overflow-hidden aspect-square bg-[#FAFAF7] border border-gold-500/40" data-testid={`ap-upload-preview-${i}`}>
                                <img src={p.url} alt="" className="w-full h-full object-cover opacity-60" />
                                <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-navy-500/10">
                                    <Loader2 className="w-6 h-6 text-white drop-shadow animate-spin" />
                                    <span className="text-[10px] font-extrabold uppercase tracking-widest text-white drop-shadow">Uploading</span>
                                </div>
                            </div>
                        ))}
                        <label className={`aspect-square rounded-md border-2 border-dashed cursor-pointer flex flex-col items-center justify-center text-navy-500/60 text-xs gap-1 bg-[#FAFAF7] ${uploading ? "border-gold-500/60 bg-gold-500/5 cursor-wait" : "border-navy-500/20 hover:border-gold-500/60"}`} data-testid="ap-upload-tile">
                            {uploading ? (
                                <>
                                    <Loader2 className="w-6 h-6 text-gold-600 animate-spin" />
                                    <span className="font-bold text-gold-700">Uploading… {uploadProgress.percent}%</span>
                                    <span className="text-[10px] text-navy-500/50">{uploadProgress.current}/{uploadProgress.total}</span>
                                </>
                            ) : (
                                <>
                                    <Upload className="w-5 h-5 text-gold-600" />
                                    <span>Upload photos</span>
                                    <span className="text-[10px] text-navy-500/45">Multiple allowed</span>
                                </>
                            )}
                            <input type="file" multiple accept="image/*" onChange={onUpload} disabled={uploading} className="hidden" data-testid="ap-image-input" />
                        </label>
                    </div>
                    {!uploading && form.images.length > 0 && (
                        <div className="mt-2 text-[11px] text-emerald-700 font-bold flex items-center gap-1" data-testid="ap-photos-ready">
                            <ArrowRight className="w-3 h-3" /> {form.images.length} photo{form.images.length > 1 ? "s" : ""} ready — you can publish
                        </div>
                    )}
                </Card>

                <button
                    type="submit"
                    disabled={submitting || uploading}
                    data-testid="ap-submit"
                    className="w-full px-6 py-4 rounded-md bg-gold-500 text-white font-semibold hover:bg-gold-600 disabled:opacity-60 disabled:cursor-not-allowed shadow-md"
                >{uploading ? "Wait for photos to finish uploading…" : submitting ? (isEdit ? "Saving…" : "Listing…") : (isEdit ? "Save Changes" : "Publish Listing")}</button>
            </form>
        </div>
    );
}

function Card({ title, children }) {
    return (
        <div className="rounded-2xl bg-white border border-navy-500/10 p-6 shadow-sm">
            <div className="label-overline mb-4">{title}</div>
            <div className="space-y-3">{children}</div>
        </div>
    );
}

function Field({ label, value, onChange, type = "text", required, testid, placeholder, textarea }) {
    const cls = "w-full bg-[#FAFAF7] border border-navy-500/15 rounded-md px-3 py-2 text-sm text-navy-500 placeholder:text-navy-500/40 focus:border-gold-500/60 focus:bg-white focus:outline-none";
    return (
        <div>
            <label className="text-xs text-navy-500/65 mb-1 block font-medium">{label}</label>
            {textarea ? (
                <textarea required={required} rows={4} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder={placeholder} className={cls} />
            ) : (
                <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder={placeholder} className={cls} />
            )}
        </div>
    );
}

function Select({ label, value, onChange, options, testid }) {
    return (
        <div>
            <label className="text-xs text-navy-500/65 mb-1 block font-medium">{label}</label>
            <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full bg-[#FAFAF7] border border-navy-500/15 rounded-md px-3 py-2 text-sm text-navy-500 focus:border-gold-500/60 focus:bg-white focus:outline-none">
                {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
        </div>
    );
}

function LocationPreview({ form, onSet }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [gpsLoading, setGpsLoading] = useState(false);
    const lat = form.latitude;
    const lng = form.longitude;

    const detect = async () => {
        setError("");
        if (!form.city && !form.locality && !form.address) {
            setError("Please fill City, Locality or Address first.");
            return;
        }
        setLoading(true);
        try {
            const r = await api.post("/geocode/preview", {
                address: form.address || null,
                locality: form.locality || null,
                city: form.city || null,
                pincode: form.pincode || null,
                state: form.state || null,
            });
            if (r.data?.found) {
                onSet(r.data.latitude, r.data.longitude);
                toast.success("Location detected! Drag the pin or tap the map to fine-tune.");
            } else {
                setError("Couldn't find that address. Drop a pin on the map manually.");
            }
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail) || "Geocoding failed. Please pin manually.");
        } finally {
            setLoading(false);
        }
    };

    const useMyGps = () => {
        setError("");
        if (!navigator.geolocation) {
            setError("Your browser doesn't support GPS location.");
            return;
        }
        setGpsLoading(true);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                onSet(pos.coords.latitude, pos.coords.longitude);
                toast.success("Pin set to your current location. Drag to adjust.");
                setGpsLoading(false);
            },
            (err) => {
                setError("Couldn't read GPS: " + (err.message || "permission denied"));
                setGpsLoading(false);
            },
            { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
        );
    };

    const clear = () => { onSet(null, null); setError(""); };

    return (
        <div className="mt-3 rounded-lg border border-navy-500/10 bg-[#FAFAF7] p-3" data-testid="ap-location-preview">
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 text-xs text-navy-500/75">
                    <MapPin className="w-4 h-4 text-gold-600" />
                    {typeof lat === "number" && typeof lng === "number" ? (
                        <span data-testid="ap-location-coords">
                            Pin: <strong className="text-navy-500">{lat.toFixed(5)}, {lng.toFixed(5)}</strong>
                        </span>
                    ) : (
                        <span>Tap on the map to drop a pin, or use one of the buttons.</span>
                    )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {typeof lat === "number" && typeof lng === "number" && (
                        <button type="button" onClick={clear} data-testid="ap-location-clear" className="text-[11px] text-navy-500/50 hover:text-red-500 underline">
                            Clear
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={useMyGps}
                        disabled={gpsLoading}
                        data-testid="ap-location-gps-btn"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-navy-500/25 text-navy-500 text-xs font-semibold hover:bg-navy-500/5 disabled:opacity-60"
                    >
                        {gpsLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MapPin className="w-3.5 h-3.5" />}
                        Use my GPS
                    </button>
                    <button
                        type="button"
                        onClick={detect}
                        disabled={loading}
                        data-testid="ap-location-detect-btn"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-gold-500 text-navy-500 text-xs font-extrabold hover:bg-gold-400 disabled:opacity-60"
                    >
                        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MapPin className="w-3.5 h-3.5" />}
                        Detect from address
                    </button>
                </div>
            </div>
            {error && <div className="mt-2 text-[11px] text-red-600">{error}</div>}
            <div className="mt-3">
                <LocationMapPicker lat={lat} lng={lng} onChange={onSet} height={288} />
                <p className="mt-2 text-[11px] text-navy-500/55 leading-relaxed">
                    <strong>Tap</strong> anywhere on the map to drop the pin, or <strong>drag</strong> the gold pin to fine-tune the exact spot of your property.
                </p>
            </div>
        </div>
    );
}

function UpgradeModal({ open, onClose, usedCount, availableCredits = 0, creditPriceInr = 99, onCreditPurchased }) {
    const [paying, setPaying] = useState(false);
    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        window.scrollTo({ top: 0, behavior: "instant" });
        return () => { document.body.style.overflow = prev; };
    }, [open]);
    if (!open) return null;

    const onPay99 = async () => {
        setPaying(true);
        try {
            const res = await startListingCreditCheckout();
            if (res.ok) {
                toast.success("Payment successful — you can now publish your next property.");
                onCreditPurchased?.();
                onClose?.();
            }
        } finally { setPaying(false); }
    };

    return createPortal(
        <div
            className="fixed inset-0 z-[10000] bg-navy-900/70 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4"
            onClick={onClose}
            data-testid="ap-upgrade-modal"
        >
            <div
                className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="shrink-0 bg-gradient-to-br from-navy-500 to-navy-700 text-white px-6 py-5 flex items-center gap-3">
                    <div className="w-11 h-11 rounded-full bg-gold-500/20 border border-gold-500/40 flex items-center justify-center shrink-0">
                        <Lock className="w-5 h-5 text-gold-400" />
                    </div>
                    <div>
                        <div className="font-heading text-lg font-bold">Your free listing is used</div>
                        <div className="text-[11px] uppercase tracking-widest text-gold-300/90 mt-0.5">Choose how to continue</div>
                    </div>
                </div>
                <div className="overflow-y-auto px-6 py-5">
                    <p className="text-sm text-navy-500 leading-relaxed">
                        Your free listing is used{typeof usedCount === "number" && <> (<strong>{usedCount}/1</strong>)</>}.
                        List your next property for just <strong className="text-gold-700">₹{creditPriceInr}</strong>, or upgrade to a subscription plan for unlimited listings.
                    </p>
                    <div className="mt-4 rounded-lg border border-gold-500/30 bg-gold-500/5 px-4 py-3">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-gold-700 font-extrabold">One-off</div>
                                <div className="font-heading text-xl font-bold text-navy-500">₹{creditPriceInr} <span className="text-xs font-semibold text-navy-500/60">per additional listing</span></div>
                            </div>
                            <Sparkles className="w-6 h-6 text-gold-500" />
                        </div>
                        <div className="text-[11px] text-navy-500/70 mt-1">
                            Instant unlock · 1 payment = 1 listing · No recurring charges
                        </div>
                    </div>
                    <ul className="mt-4 space-y-2 text-sm text-navy-500/80">
                        <li className="flex items-start gap-2"><Sparkles className="w-4 h-4 text-gold-500 mt-0.5 shrink-0" /> Or subscribe for unlimited property listings</li>
                        <li className="flex items-start gap-2"><Sparkles className="w-4 h-4 text-gold-500 mt-0.5 shrink-0" /> Featured placement + verified badge</li>
                        <li className="flex items-start gap-2"><Sparkles className="w-4 h-4 text-gold-500 mt-0.5 shrink-0" /> Priority support & faster lead responses</li>
                    </ul>
                    {availableCredits > 0 && (
                        <div className="mt-3 rounded-md bg-emerald-500/10 border border-emerald-500/30 px-3 py-2 text-[12px] text-emerald-800">
                            You already have <strong>{availableCredits} paid credit{availableCredits > 1 ? "s" : ""}</strong> available — just close this and hit Publish.
                        </div>
                    )}
                </div>
                <div className="shrink-0 border-t border-navy-500/10 px-6 py-3 flex flex-col sm:flex-row gap-2 bg-white">
                    <button
                        type="button"
                        onClick={onPay99}
                        disabled={paying}
                        data-testid="ap-pay-per-listing-cta"
                        className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md bg-gold-500 text-navy-900 text-sm font-extrabold hover:bg-gold-400 disabled:opacity-60"
                    >
                        {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Pay ₹{creditPriceInr}</>}
                    </button>
                    <Link
                        to="/pricing"
                        data-testid="ap-upgrade-modal-cta"
                        className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md border-2 border-navy-500/20 text-navy-500 text-sm font-extrabold hover:bg-navy-500/5"
                    >
                        View Subscription Plans <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </div>
        </div>,
        document.body
    );
}

